import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "runningPanel",
    "transitionPanel",
    "statusBadge",
    "currentTime",
    "nextPreview",
    "transitionTarget",
    "pauseButton",
    "activityList",
  ]

  connect() {
    this.blocks = [
      { label: "Seated work", seconds: 45 },
      { label: "Standing work", seconds: 15 },
      { label: "Rest / stretch", seconds: 5 },
    ]
    this.index = 0
    this.remaining = this.blocks[0].seconds
    this.state = "running"
    this.render()
    this.interval = setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    clearInterval(this.interval)
  }

  tick() {
    if (this.state !== "running") return

    this.remaining -= 1
    if (this.remaining <= 0) {
      this.state = "transition"
    }
    this.render()
  }

  confirmSwitch() {
    this.index = (this.index + 1) % this.blocks.length
    this.remaining = this.blocks[this.index].seconds
    this.state = "running"
    this.render()
  }

  togglePause() {
    if (this.state === "transition") return

    this.state = this.state === "running" ? "paused" : "running"
    this.render()
  }

  skip() {
    if (this.state === "transition") {
      this.confirmSwitch()
      return
    }

    this.index = (this.index + 1) % this.blocks.length
    this.remaining = this.blocks[this.index].seconds
    this.state = "running"
    this.render()
  }

  render() {
    const current = this.blocks[this.index]
    const next = this.blocks[(this.index + 1) % this.blocks.length]
    const inTransition = this.state === "transition"
    const transitionNext = this.blocks[(this.index + 1) % this.blocks.length]

    this.runningPanelTarget.classList.toggle("hidden", inTransition)
    this.transitionPanelTarget.classList.toggle("hidden", !inTransition)
    this.transitionPanelTarget.classList.toggle("flex", inTransition)

    if (!inTransition) {
      const status = this.state === "paused" ? "Paused" : "Running"
      this.statusBadgeTarget.textContent = `${current.label} • ${this.formatDuration(current.seconds)} • ${status}`
      this.currentTimeTarget.textContent = this.formatTime(this.remaining)
      this.nextPreviewTarget.textContent = `Next: ${next.label} (${this.formatDuration(next.seconds)})`
      this.pauseButtonTarget.textContent = this.state === "paused" ? "Resume" : "Pause"
    } else {
      this.transitionTargetTarget.textContent = transitionNext.label
    }

    this.activityListTarget.innerHTML = this.blocks
      .map((block, i) => this.activityItemHtml(block, i))
      .join("")
  }

  activityItemHtml(block, i) {
    const inTransition = this.state === "transition"
    const completed = i < this.index
    const active = inTransition ? i === this.index : i === this.index

    const stateClass = active
      ? "border border-success/40 bg-base-300/40"
      : completed
        ? "border border-base-300 opacity-80"
        : "border border-base-300 opacity-50"

    const marker = completed
      ? '<span class="text-success text-sm leading-none">✓</span>'
      : active
        ? '<span class="status status-success status-sm"></span>'
        : '<span class="size-2 rounded-full bg-base-content/20"></span>'

    return `
      <li class="flex items-center gap-3 rounded-box px-4 py-3 ${stateClass}">
        <span class="shrink-0 w-4 flex justify-center">${marker}</span>
        <div class="flex-1 min-w-0">
          <p class="font-medium truncate">${block.label}</p>
          <p class="text-xs text-base-content/50">${this.formatDuration(block.seconds)}</p>
        </div>
      </li>
    `
  }

  formatTime(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  formatDuration(totalSeconds) {
    if (totalSeconds >= 60) {
      const minutes = Math.floor(totalSeconds / 60)
      return minutes === 1 ? "1 min" : `${minutes} mins`
    }
    return `${totalSeconds} secs`
  }
}
